/* tasting-calendar.js — Phase 2 frontend (mock-data capable) */
if (!customElements.get('tasting-calendar')) {
  customElements.define(
    'tasting-calendar',
    class TastingCalendar extends HTMLElement {
      constructor() {
        super();

        const scheduleEl = this.parentElement.querySelector('script[data-tasting-schedule]');
        const configEl = this.parentElement.querySelector('script[data-tasting-config]');
        if (!scheduleEl || !configEl) {
          this.renderError('Schedule data missing.');
          return;
        }

        try {
          this.schedule = JSON.parse(scheduleEl.textContent);
          this.config = JSON.parse(configEl.textContent);
        } catch (e) {
          this.renderError('Schedule data is invalid JSON.');
          return;
        }

        this.dayIndex = {
          sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
          thursday: 4, friday: 5, saturday: 6,
        };

        this.tz = this.schedule.timezone || 'Africa/Johannesburg';
        this.todayISO = this.todayInTz();
        this.cursor = this.firstOfMonth(this.todayISO);
        this.minMonth = this.firstOfMonth(this.todayISO);
        this.maxDateISO = this.addDays(this.todayISO, (this.schedule.weeks_ahead_visible || 8) * 7);
        this.maxMonth = this.firstOfMonth(this.maxDateISO);

        this.availability = {};
        this.availabilityRange = null;
        this.selectedDate = null;
        this.selectedSlot = null;
        this.quantity = 1;
        this.busy = false;

        this.bookableDow = new Set(this.schedule.slots.map(s => this.dayIndex[s.day_of_week]));
        this.blockedSet = new Set(this.schedule.blocked_dates || []);

        this.render();
        this.fetchAvailability();
      }

      /* ---------- Date helpers (string ISO) ---------- */
      todayInTz() {
        const fmt = new Intl.DateTimeFormat('en-CA', {
          timeZone: this.tz, year: 'numeric', month: '2-digit', day: '2-digit',
        });
        return fmt.format(new Date());
      }

      nowInTz() {
        const fmt = new Intl.DateTimeFormat('en-CA', {
          timeZone: this.tz,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: false,
        });
        const parts = fmt.formatToParts(new Date());
        const get = (t) => parts.find(p => p.type === t).value;
        return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` };
      }

      firstOfMonth(iso) { return iso.slice(0, 7) + '-01'; }

      addDays(iso, n) {
        const [y, m, d] = iso.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        dt.setUTCDate(dt.getUTCDate() + n);
        return dt.toISOString().slice(0, 10);
      }

      addMonths(iso, n) {
        const [y, m] = iso.split('-').map(Number);
        const total = (y * 12 + (m - 1)) + n;
        const ny = Math.floor(total / 12);
        const nm = (total % 12) + 1;
        return `${ny}-${String(nm).padStart(2, '0')}-01`;
      }

      dowOfISO(iso) {
        const [y, m, d] = iso.split('-').map(Number);
        return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
      }

      daysInMonth(iso) {
        const [y, m] = iso.split('-').map(Number);
        return new Date(Date.UTC(y, m, 0)).getUTCDate();
      }

      /* ---------- Logic ---------- */
      isPastCutoff(dateISO, timeHHMM) {
        const cutoffH = this.schedule.booking_cutoff_hours || 0;
        const [y, mo, d] = dateISO.split('-').map(Number);
        const [h, mi] = timeHHMM.split(':').map(Number);
        const slotMs = Date.UTC(y, mo - 1, d, h - 2, mi); // SAST = UTC+2
        const nowMs = Date.now();
        return (slotMs - nowMs) < cutoffH * 3600 * 1000;
      }

      slotsForDate(dateISO) {
        const dow = this.dowOfISO(dateISO);
        return this.schedule.slots
          .filter(s => this.dayIndex[s.day_of_week] === dow)
          .sort((a, b) => a.start_time.localeCompare(b.start_time));
      }

      isDateBookable(dateISO) {
        if (dateISO < this.todayISO) return false;
        if (dateISO > this.maxDateISO) return false;
        if (this.blockedSet.has(dateISO)) return false;
        if (!this.bookableDow.has(this.dowOfISO(dateISO))) return false;
        const slots = this.slotsForDate(dateISO);
        return slots.some(s => {
          if (this.isPastCutoff(dateISO, s.start_time)) return false;
          const av = this.availability[dateISO]?.[s.start_time];
          if (av) return av.remaining > 0;
          return true; // no availability data yet → assume bookable
        });
      }

      formatPrice(amount) {
        return new Intl.NumberFormat(undefined, {
          style: 'currency', currency: this.config.currency || 'ZAR',
          minimumFractionDigits: 0, maximumFractionDigits: 2,
        }).format(amount);
      }

      formatLongDate(iso) {
        const [y, m, d] = iso.split('-').map(Number);
        return new Intl.DateTimeFormat(undefined, {
          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        }).format(new Date(Date.UTC(y, m - 1, d)));
      }

      /* ---------- Fetch ---------- */
      async fetchAvailability() {
        const from = this.firstOfMonth(this.cursor);
        const to = this.addDays(this.addMonths(from, 1), -1);
        if (this.availabilityRange && from >= this.availabilityRange.from && to <= this.availabilityRange.to) return;

        const baseUrl = this.config.use_mock ? this.config.mock_url : this.config.availability_path;
        const url = this.config.use_mock ? baseUrl : `${baseUrl}?from=${from}&to=${to}`;
        try {
          const res = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          const data = await res.json();
          this.availability = { ...this.availability, ...data };
          this.availabilityRange = { from, to };
          this.renderCalendarGrid();
          if (this.selectedDate) this.renderSlotPanel();
        } catch (e) {
          this.setMessage('Could not load availability. Please refresh.', true);
        }
      }

      /* ---------- Render ---------- */
      renderError(msg) {
        this.innerHTML = `<p class="tasting-calendar__error">${msg}</p>`;
      }

      render() {
        this.innerHTML = `
          <div class="tasting-calendar__layout">
            <div class="tasting-calendar__cal">
              <div class="tasting-calendar__cal-head">
                <button type="button" class="tasting-calendar__nav" data-action="prev" aria-label="Previous month">
                  <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M8 1L3 6l5 5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
                </button>
                <h3 class="tasting-calendar__cal-title" data-cal-title aria-live="polite"></h3>
                <button type="button" class="tasting-calendar__nav" data-action="next" aria-label="Next month">
                  <svg viewBox="0 0 12 12" aria-hidden="true"><path d="M4 1l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
                </button>
              </div>
              <div class="tasting-calendar__grid" role="grid" data-grid></div>
              <span class="tasting-calendar__sr" aria-live="polite" data-sr></span>
            </div>
            <div class="tasting-calendar__panel" data-panel></div>
          </div>
        `;

        this.$ = (sel) => this.querySelector(sel);
        this.$('[data-action="prev"]').addEventListener('click', () => this.navigate(-1));
        this.$('[data-action="next"]').addEventListener('click', () => this.navigate(1));
        this.$('[data-grid]').addEventListener('keydown', (e) => this.onGridKey(e));

        this.renderCalendarGrid();
        this.renderSlotPanel();
      }

      renderCalendarGrid() {
        this.$('[data-cal-title]').textContent = new Intl.DateTimeFormat(undefined, {
          month: 'long', year: 'numeric',
        }).format(new Date(this.cursor + 'T00:00:00Z'));
        this.$('[data-action="prev"]').disabled = this.cursor <= this.minMonth;
        this.$('[data-action="next"]').disabled = this.cursor >= this.maxMonth;

        const grid = this.$('[data-grid]');
        const dows = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        let html = dows.map(d => `<div class="tasting-calendar__dow" role="columnheader">${d}</div>`).join('');

        const startDow = this.dowOfISO(this.cursor);
        const days = this.daysInMonth(this.cursor);

        for (let d = 1; d <= days; d++) {
          const iso = `${this.cursor.slice(0, 8)}${String(d).padStart(2, '0')}`;
          const bookable = this.isDateBookable(iso);
          const selected = iso === this.selectedDate;
          const cls = [
            'tasting-calendar__day',
            bookable ? 'tasting-calendar__day--bookable' : '',
            selected ? 'tasting-calendar__day--selected' : '',
          ].filter(Boolean).join(' ');
          const colStart = (d === 1) ? ` style="grid-column-start: ${startDow + 1};"` : '';
          html += `<button type="button" class="${cls}" role="gridcell" aria-selected="${selected}" data-date="${iso}" ${bookable ? '' : 'disabled'} tabindex="${selected || (!this.selectedDate && d === 1) ? 0 : -1}"${colStart}>${d}</button>`;
        }

        grid.innerHTML = html;
        grid.querySelectorAll('[data-date]').forEach(btn => {
          btn.addEventListener('click', () => this.selectDate(btn.dataset.date));
        });
      }

      renderSlotPanel() {
        const panel = this.$('[data-panel]');
        if (!this.selectedDate) {
          panel.innerHTML = `<p class="tasting-calendar__panel-empty">Pick a date to see available times.</p>`;
          return;
        }

        const slots = this.slotsForDate(this.selectedDate);
        const slotHTML = slots.map(s => {
          const av = this.availability[this.selectedDate]?.[s.start_time];
          const remaining = av ? av.remaining : s.capacity;
          const past = this.isPastCutoff(this.selectedDate, s.start_time);
          const full = remaining <= 0;
          const disabled = past || full;
          const selected = this.selectedSlot && this.selectedSlot.start_time === s.start_time;
          const cls = [
            'tasting-calendar__slot',
            full ? 'tasting-calendar__slot--full' : '',
            selected ? 'tasting-calendar__slot--selected' : '',
          ].filter(Boolean).join(' ');
          const meta = past ? 'Booking closed' : full ? 'Sold out' : `${remaining} seat${remaining === 1 ? '' : 's'} left`;
          return `<button type="button" class="${cls}" data-slot-start="${s.start_time}" data-slot-end="${s.end_time}" ${disabled ? 'disabled' : ''} aria-pressed="${selected}">
            <span class="tasting-calendar__slot-time">${s.start_time}&ndash;${s.end_time}</span>
            <span class="tasting-calendar__slot-meta">${meta}</span>
          </button>`;
        }).join('');

        const picker = this.selectedSlot ? this.renderQtyPicker() : '';

        panel.innerHTML = `
          <h3 class="tasting-calendar__panel-date">${this.formatLongDate(this.selectedDate)}</h3>
          <div class="tasting-calendar__slots" role="list">${slotHTML}</div>
          ${picker}
          <p class="tasting-calendar__msg" data-msg aria-live="polite"></p>
        `;

        panel.querySelectorAll('[data-slot-start]').forEach(btn => {
          btn.addEventListener('click', () => this.selectSlot(btn.dataset.slotStart, btn.dataset.slotEnd));
        });

        if (this.selectedSlot) this.wireQtyPicker(panel);
      }

      renderQtyPicker() {
        const av = this.availability[this.selectedDate]?.[this.selectedSlot.start_time];
        const remaining = av ? av.remaining : this.selectedSlot.capacity;
        const max = Math.min(remaining, this.schedule.max_per_booking || 10);
        if (this.quantity > max) this.quantity = max;
        if (this.quantity < 1) this.quantity = 1;
        const total = this.quantity * (this.schedule.price_per_seat || 0);
        return `
          <div class="tasting-calendar__qty-row">
            <span class="tasting-calendar__qty-label">Seats</span>
            <div class="tasting-calendar__qty">
              <button type="button" class="tasting-calendar__qty-btn" data-qty="-" aria-label="Decrease seats" ${this.quantity <= 1 ? 'disabled' : ''}>&minus;</button>
              <input class="tasting-calendar__qty-input" type="number" min="1" max="${max}" value="${this.quantity}" data-qty-input aria-label="Number of seats" />
              <button type="button" class="tasting-calendar__qty-btn" data-qty="+" aria-label="Increase seats" ${this.quantity >= max ? 'disabled' : ''}>+</button>
            </div>
          </div>
          <div class="tasting-calendar__total">
            <span class="tasting-calendar__total-label">Total</span>
            <span class="tasting-calendar__total-value">${this.formatPrice(total)}</span>
          </div>
          <button type="button" class="tasting-calendar__book button button--primary" data-book ${this.busy ? 'disabled' : ''}>${this.busy ? 'Booking&hellip;' : 'Book now'}</button>
        `;
      }

      wireQtyPicker(panel) {
        panel.querySelector('[data-qty="-"]').addEventListener('click', () => { this.quantity--; this.renderSlotPanel(); });
        panel.querySelector('[data-qty="+"]').addEventListener('click', () => { this.quantity++; this.renderSlotPanel(); });
        panel.querySelector('[data-qty-input]').addEventListener('change', (e) => {
          this.quantity = parseInt(e.target.value, 10) || 1;
          this.renderSlotPanel();
        });
        panel.querySelector('[data-book]').addEventListener('click', () => this.book());
      }

      /* ---------- Interactions ---------- */
      navigate(delta) {
        const next = this.addMonths(this.cursor, delta);
        if (next < this.minMonth || next > this.maxMonth) return;
        this.cursor = next;
        this.fetchAvailability();
        this.renderCalendarGrid();
      }

      onGridKey(e) {
        const target = e.target.closest('[data-date]');
        if (!target) return;
        const iso = target.dataset.date;
        const map = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
        if (!(e.key in map)) return;
        e.preventDefault();
        const next = this.addDays(iso, map[e.key]);
        if (next.slice(0, 7) !== this.cursor.slice(0, 7)) {
          this.cursor = this.firstOfMonth(next);
          this.fetchAvailability();
          this.renderCalendarGrid();
        }
        const btn = this.querySelector(`[data-date="${next}"]`);
        if (btn) btn.focus();
      }

      selectDate(iso) {
        this.selectedDate = iso;
        this.selectedSlot = null;
        this.quantity = 1;
        this.renderCalendarGrid();
        this.renderSlotPanel();
        this.announce(`Selected ${this.formatLongDate(iso)}.`);
      }

      selectSlot(start, end) {
        const slot = this.schedule.slots.find(s => s.start_time === start &&
          this.dayIndex[s.day_of_week] === this.dowOfISO(this.selectedDate));
        if (!slot) return;
        this.selectedSlot = { ...slot, start_time: start, end_time: end };
        this.quantity = 1;
        this.renderSlotPanel();
        this.announce(`Selected ${start} to ${end}.`);
      }

      announce(text) {
        const sr = this.$('[data-sr]');
        if (sr) sr.textContent = text;
      }

      setMessage(text, isError) {
        const msg = this.querySelector('[data-msg]');
        if (!msg) return;
        msg.textContent = text;
        msg.classList.toggle('tasting-calendar__msg--error', !!isError);
      }

      /* ---------- Booking ---------- */
      async book() {
        if (this.busy || !this.selectedDate || !this.selectedSlot) return;
        this.busy = true;
        this.renderSlotPanel();
        this.setMessage('Adding to cart…');
        try {
          await fetch('/cart/clear.js', { method: 'POST' });

          const formData = {
            items: [{
              id: parseInt(this.config.variant_id, 10),
              quantity: this.quantity,
              properties: {
                'Tasting Date': this.selectedDate,
                'Tasting Time': this.selectedSlot.start_time,
              },
            }],
          };
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(formData),
          });
          const data = await res.json().catch(() => null);
          if (!res.ok) {
            throw new Error((data && data.description) || `Could not add to cart (HTTP ${res.status}).`);
          }

          const target = this.config.redirect_to === 'cart' ? '/cart' : '/checkout';
          window.location.href = target;
        } catch (e) {
          this.busy = false;
          this.setMessage(e.message || 'Booking failed. Please try again.', true);
          this.renderSlotPanel();
        }
      }
    }
  );
}
